import { t, languageSelect, getLanguage } from "../core/i18n.js";
import { icon } from "./icons.js";
import { dayxMark } from "./brand.js";
import { guideOverview } from "./public-guides.js";
import { legalLinks } from "./legal-pages.js";
import { featureDemo, bindLandingDemos } from "./landing-demos.js";
import { demoCursor, landingTour } from "./landing-tour.js";
import { syncLandingAccount } from "./landing-account.js";
import { fullscreenButton } from "./fullscreen.js";
import { getSyncInfo } from "../services/storage.js";
export { dayxMark } from "./brand.js";

let headerObserver;
const landingLinks = () =>
  [
    ["the-system", "The tools"],
    ["in-action", "In action"],
    ["how-it-works", "How it works"],
    ["guides", "Guides"],
    ["pricing", "Pricing"],
  ]
    .map(([id, label]) => `<a href="#${id}">${t(label)}</a>`)
    .join("");

export function landingHTML() {
  return `
    <a class="skip-link" href="#welcomeContent">${t("Skip to content")}</a>
    <header class="dayx-landing-nav">
      <a class="wordmark" href="/" data-home aria-label="${t("DayX home")}">${dayxMark}</a>
      <nav class="landing-desktop-links" aria-label="${t("Workspace navigation")}">${landingLinks()}</nav>
      <details class="landing-mobile-menu"><summary aria-label="${t("Open menu")}">${icon("all")}<span>${t("Menu")}</span></summary><nav aria-label="${t("Workspace navigation")}">${landingLinks()}</nav></details>
      <div class="landing-account-links">${fullscreenButton()}${languageSelect()}<a class="landing-login" href="/login">${t("Log in")}</a><a class="landing-signup" href="/signup">${t("Sign up")} ${icon("arrow")}</a></div>
    </header>
    <main id="welcomeContent">
      <section class="dayx-hero" aria-labelledby="welcomeTitle">
        <div class="dayx-hero-copy"><div class="landing-kicker"><span class="status-dot"></span> ${t("Your day. Your direction.")}</div>
          <h1 id="welcomeTitle">${t("Make time for what matters.")}</h1>
          <p>${t("Turn your plans into progress with a daily planner, a focus timer and a clear view of your goals. All in DayX.")}</p>
          <div class="landing-actions"><a class="landing-primary" href="/signup">${t("Start planning")} ${icon("arrow")}</a><a class="landing-secondary" href="/today" data-enter="today">${t("Explore DayX")} <span aria-hidden="true">↗</span></a></div>
          <div class="dayx-hero-note"><span>✓</span> ${t("Uzbek, English and Russian")}<i></i><b>$1</b> ${t("/ month")}</div>
        </div>
        <div class="dayx-preview-stage" data-demo="hero">
          <span class="preview-orbit orbit-a" aria-hidden="true"></span><span class="preview-orbit orbit-b" aria-hidden="true"></span>
          <div class="dayx-product-preview" aria-label="${t("Example workspace")}">
            <div class="preview-nav">${dayxMark}<span>${t("Example workspace")}</span><i></i><i></i><i></i></div>
            <div class="preview-body"><div class="preview-day"><span>${t("Today’s plan")}</span><b>${t("Ready for your next step")}</b><span class="preview-chip">${t("2 of 3 complete")}</span></div>
              <div class="preview-task done"><span>${icon("check")}</span><div><span class="demo-task-name">${t("Read for 20 minutes")}</span><small>08:00 — 08:20</small></div>${icon("today")}</div>
              <div class="preview-task done"><span>${icon("check")}</span><div><span class="demo-task-name">${t("Practice English")}</span><small>09:00 — 09:25</small></div>${icon("check")}</div>
              <div class="preview-task"><span>${icon("check")}</span><div><span class="demo-task-name">${t("Work on your main goal")}</span><small>10:00 — 10:50</small></div>${icon("spark")}</div>
              <div class="preview-bottom"><span>${t("Daily focus")}<b data-demo-time>25:00</b></span><div class="preview-play">${icon("play")}</div><div class="preview-bars" aria-hidden="true">${[40, 65, 48, 85, 60, 100, 74, 90, 67, 95, 75, 100].map((h, index) => `<i style="height:${h}%;--delay:${index * -0.18}s"></i>`).join("")}</div></div>
            </div>
          </div>
          ${demoCursor}
          <div class="preview-floating">${icon("spark")}<span><span data-demo-caption>${t("Every day, a step forward.")}</span><strong>${t("Plan, focus, grow.")}</strong></span></div>
        </div>
      </section>
      <div class="dayx-value-strip"><span>${icon("today")}${t("Daily and weekly planning")}</span><span>${icon("pomodoro")}${t("Focus and study timers")}</span><span>${icon("table")}${t("30-day plan table")}</span></div>
      <section class="dayx-tools" id="the-system" aria-labelledby="systemTitle"><div class="dayx-section-intro"><div class="landing-eyebrow">01 / ${t("Plans. Focus. Progress.")}</div><h2 id="systemTitle">${t("One space for a better day.")}</h2><p>${t("Less switching. More doing. A set of tools that work together, at your pace.")}</p></div>
        <div class="dayx-feature-grid">${[
          [
            "today",
            "Plan with intention.",
            "Choose a routine that fits your days. Add a plan, set a time and check it off when you finish.",
            "table",
          ],
          [
            "pomodoro",
            "Focus on one thing.",
            "Pomodoro sessions and automatic study sequences help you protect your time.",
            "pomodoro",
          ],
          [
            "all",
            "Make progress visible.",
            "Check off completed plans, review your week and keep your exam dates in sight.",
            "weekly",
          ],
        ]
          .map(
            ([glyph, title, description, route], i) =>
              `<article class="dayx-feature ${i === 1 ? "dark" : ""}"><div class="dayx-feature-icon">${icon(glyph)}</div><span class="feature-index">0${i + 1}</span>${featureDemo(i)}<h3>${t(title)}</h3><p>${t(description)}</p><a href="/${route}" data-enter="${route}" aria-label="${t(title)}">${icon("arrow")}</a></article>`,
          )
          .join("")}</div>
      </section>
      ${landingTour()}
      <section class="dayx-how" id="how-it-works" aria-labelledby="howTitle"><div class="dayx-section-intro"><div class="landing-eyebrow">03 / ${t("How it works")}</div><h2 id="howTitle">${t("From an idea to a daily rhythm.")}</h2></div><ol>${[
        [
          "Add your first plan.",
          "Choose what matters today and give it a place in your routine.",
        ],
        [
          "Give it your focus.",
          "Start a timer. Work at your pace. Take a real break.",
        ],
        [
          "Keep moving forward.",
          "Your completed plans tell the story. Build on them tomorrow.",
        ],
      ]
        .map(
          ([title, description], i) =>
            `<li><span>0${i + 1}</span><div><h3>${t(title)}</h3><p>${t(description)}</p></div></li>`,
        )
        .join("")}</ol></section>
      ${guideOverview(getLanguage())}
      <section class="dayx-pricing" id="pricing" aria-labelledby="pricingTitle"><div class="dayx-price-copy"><div class="landing-eyebrow">05 / ${t("Pricing")}</div><h2 id="pricingTitle">${t("Simple by design. Fair by price.")}</h2><p>${t("Your entire daily workspace for one dollar a month.")}</p><div class="price-detail">${icon("check")} ${t("Payments are not enabled yet. You can continue using your workspace.")}</div></div><article class="dayx-price-card"><div class="price-top"><span>${t("DayX monthly")}</span><span class="price-badge">${t("Monthly plan")}</span></div><div class="dayx-price"><b>$1</b><span>${t("/ month")}</span></div><p>${t("One plan. Every essential tool.")}</p><ul>${["Daily and weekly planning", "30-day plan table", "Focus and study timers", "Exam dates and progress tracking", "Uzbek, English and Russian"].map((text) => `<li>${icon("check")}${t(text)}</li>`).join("")}</ul><a class="landing-primary" href="/signup">${t("Create your DayX account")} ${icon("arrow")}</a><small>${t("Price preview · payments coming later")}</small></article></section>
      <section class="dayx-final-cta"><span class="cta-spark" aria-hidden="true">${icon("spark")}</span><h2>${t("Your next chapter starts today.")}</h2><p>${t("One plan. One focused session. One step closer to your goals.")}</p><a class="landing-primary" href="/signup">${t("Start planning")} ${icon("arrow")}</a></section>
    </main>
    <footer class="dayx-footer"><a class="wordmark" href="/" data-home>${dayxMark}</a><span>© ${new Date().getFullYear()} DayX. ${t("All rights reserved.")}</span><div><a href="#pricing">${t("Pricing")}</a><a href="/login">${t("Log in")}</a>${legalLinks(getLanguage())}${languageSelect()}</div></footer>`;
}

export function renderLanding() {
  const welcome = document.getElementById("welcome");
  welcome.innerHTML = landingHTML();
  welcome
    .querySelectorAll('a[href="/signup"]')
    .forEach((link) => (link.dataset.accountCta = "true"));
  refreshLandingAccount();
  const header = welcome.querySelector(".dayx-landing-nav");
  header.classList.toggle("is-scrolled", window.scrollY > 8);
  headerObserver?.disconnect();
  headerObserver = new ResizeObserver(() => {
    if (header.offsetHeight)
      welcome.style.setProperty(
        "--landing-header-height",
        `${header.offsetHeight}px`,
      );
  });
  headerObserver.observe(header);
  const menu = header.querySelector(".landing-mobile-menu");
  menu.addEventListener("click", (event) => {
    if (event.target.closest("a")) menu.open = false;
  });
  menu.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      menu.open = false;
      menu.querySelector("summary").focus();
    }
  });
  header.addEventListener("click", (event) => {
    if (!menu.contains(event.target)) menu.open = false;
  });
  bindLandingDemos(welcome);
}

export function refreshLandingAccount() {
  const { session, requiresLogin } = getSyncInfo();
  syncLandingAccount(
    document.getElementById("welcome"),
    requiresLogin ? null : session?.user,
  );
}
